import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, LogIn } from 'lucide-react';
import { SITE } from '@/data/site';
import { Lotus } from './ui/Lotus';

const LINKS = [
  { to: '/', label: 'Home', end: true },
  { to: '/services', label: 'Treatments' },
  { to: '/booking', label: 'Booking' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/stay', label: 'Stay' },
  { to: '/cars', label: 'Car Hire' },
  { to: '/gift-cards', label: 'Gift Cards' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

export function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  // Pages with a dark full-bleed hero — keep the nav transparent at the top.
  const heroPage = pathname === '/' || pathname === '/spa';
  const solid = scrolled || !heroPage;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid ? 'bg-espresso-deep/95 shadow-card backdrop-blur' : 'bg-transparent'
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 sm:px-10 sm:py-6">
        <Link to="/" className="flex select-none items-center gap-3 text-left leading-none" aria-label={`${SITE.name} home`}>
          <Lotus size={40} className="text-gold" />
          <span className="wordmark block text-[40px] leading-[0.9] text-gold sm:text-[46px]">{SITE.wordmark}</span>
        </Link>

        <div className="hidden items-center gap-7 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `relative font-body text-sm font-medium tracking-wide text-cream/90 transition hover:text-gold after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-gold after:transition-all ${
                  isActive ? '!text-gold after:w-full' : 'after:w-0 hover:after:w-full'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/book" className="btn-gold !px-6 !py-2.5">Book Now</Link>
          <Link to="/admin-login" aria-label="Staff login" title="Staff login" className="text-cream/70 transition hover:text-gold">
            <LogIn size={20} />
          </Link>
        </div>

        <button className="text-gold lg:hidden" onClick={() => setOpen((v) => !v)} aria-label={open ? 'Close menu' : 'Open menu'}>
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-cream/10 bg-espresso-deep/98 backdrop-blur lg:hidden">
          <div className="flex flex-col px-6 py-3">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `border-l-2 px-3 py-3 font-body text-sm font-medium transition ${
                    isActive ? 'border-gold bg-gold/10 text-gold' : 'border-transparent text-cream/90 hover:text-gold'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/book" className="btn-gold mt-3 w-full">Book Now</Link>
            <Link to="/admin-login" className="mt-2 flex items-center justify-center gap-2 px-3 py-3 font-body text-xs uppercase tracking-wide text-cream/60 hover:text-gold">
              <LogIn size={15} /> Staff login
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
